import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * WORDS FROM CLIENTS — an overlapping deck of same-size cards.
 *
 * Mobile (< lg): tight stack, staggered mostly DOWNWARDS (only each card's name
 * peeks). Unchanged — the layout the client approved.
 *
 * Desktop (lg+): SAME card size, but the deck is pulled apart SIDEWAYS
 * (horizontal stagger) so the three cards spread into the width and overlap
 * much less than the mobile stack — while still overlapping.
 *
 * Tap a card to bring it to the front; swipe the front card left/right to cycle.
 */
const ALEXANDRA = [
  `Unlike astrology readings that rely mostly on written descriptions, this experience was visual, intuitive, and instantly recognizable. The colors, moods, textures, and overall aesthetic of the board reflected parts of me that I've always felt but never quite knew how to visualize, let alone articulate.`,
  `The moment I saw it, I thought, "This is me." It captured both the energy I project to the world and the deeper emotional landscape that I carry within. The board helped me better understand my personal style, my inner moods, and how these qualities are reflected through the way I dress and present myself.`,
  `What made the experience even more valuable was receiving practical guidance based on my birth chart, insights into how I naturally express myself and how I can align my wardrobe and visual identity with who I truly am. I would highly recommend it to anyone looking for a deeper, more visual understanding of themselves.`,
];
const ZSUZSA = [
  `I expected something beautiful, but I didn't expect to feel so deeply understood. My Soulboard put words, colours and imagery to parts of myself that I had always sensed but could never fully explain.`,
  `It didn't feel like discovering someone new. It felt like remembering who I'd always been. That alone brought so much clarity.`,
  `Since receiving it, I've found myself looking at my choices differently and feeling more connected to the person I already am. It's something I know I'll keep coming back to whenever I need to reconnect with myself.`,
  `And now I genuinely can't wait for my Embodied Styling Session with Melinda. It feels like the natural next step, and I'm excited to see how everything we've uncovered will come to life.`,
];

const CARD_H = 420;
const CARD_W_DESKTOP = 548; // same width the card had originally

// Mobile: near-vertical stack (approved). Desktop: fanned SIDEWAYS, less overlap.
const MOBILE_SLOTS = [
  { top: 0,   left: 0,  rotate: -1.2, z: 30 },
  { top: 64,  left: 14, rotate: 1.4,  z: 20 },
  { top: 128, left: 28, rotate: -0.8, z: 10 },
];
const DESKTOP_SLOTS = [
  { top: 0,   left: 0,   rotate: -2,   z: 30 },
  { top: 55,  left: 165, rotate: 1.6,  z: 20 },
  { top: 110, left: 330, rotate: -1,   z: 10 },
];

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isDesktop;
};

const QuoteMark = () => (
  <svg className="w-9 h-9 text-[#d4ff00] mb-4 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9.983 3v7.391c0 5.704-3.731 9.57-8.983 10.609l-.995-2.151c2.432-.917 3.995-3.638 3.995-5.849h-4v-10h9.983zm14.017 0v7.391c0 5.704-3.748 9.57-9 10.609l-.996-2.151c2.433-.917 3.996-3.638 3.996-5.849h-3.983v-10h9.983z" />
  </svg>
);

const CardInner = ({ item }) => (
  <>
    <QuoteMark />
    <div className="space-y-3 text-neutral-800 italic text-sm lg:text-base leading-relaxed overflow-y-auto flex-1 pr-1">
      {item.quotes.map((q, i) => (
        <p key={i} data-testid={`testimonial-${item.id}-${i + 1}`}>
          {q}
        </p>
      ))}
    </div>
    <div className="mt-4 pt-3 border-t border-neutral-100 shrink-0 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
      <p className="text-sm uppercase tracking-[0.3em] text-neutral-600 flex items-center gap-2" data-testid={`testimonial-${item.id}-author`}>
        <span aria-hidden className="inline-block w-5 h-px bg-neutral-400" />
        <span className="font-bold text-neutral-900">{item.author}</span>
      </p>
      <span
        className="text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1"
        style={{ background: '#d4ff00', color: '#171717' }}
        data-testid={`testimonial-${item.id}-service`}
      >
        {item.service}
      </span>
    </div>
  </>
);

const TestimonialsSection = () => {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const isDesktop = useIsDesktop();

  const testimonials = [
    {
      id: 'kriszti',
      quotes: [t('krisztiQuote'), t('krisztiQuote2'), t('krisztiQuote3')],
      author: t('krisztiAuthor'),
      service: 'Signature Soulboard',
    },
    { id: 'client2', quotes: ALEXANDRA, author: 'Alexandra', service: 'Signature Soulboard' },
    { id: 'client3', quotes: ZSUZSA, author: 'Zsuzsa', service: 'Signature Soulboard' },
  ];
  const total = testimonials.length;
  const next = () => setActiveIndex((p) => (p + 1) % total);
  const prev = () => setActiveIndex((p) => (p - 1 + total) % total);

  const SLOTS = isDesktop ? DESKTOP_SLOTS : MOBILE_SLOTS;
  const maxTop = Math.max(...SLOTS.map((s) => s.top));
  const maxLeft = Math.max(...SLOTS.map((s) => s.left));
  const cardW = isDesktop ? CARD_W_DESKTOP : 'calc(100% - 28px)';
  const stackW = isDesktop ? maxLeft + CARD_W_DESKTOP : '100%';

  return (
    <section id="testimonials" className="py-24 bg-[#faf7ee]/40 overflow-hidden" data-testid="testimonials-section">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-xl lg:max-w-5xl mx-auto">
          <motion.h2
            className="text-5xl mb-12 text-center text-neutral-900"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {t('testimonialsTitle')}
          </motion.h2>

          {/* Overlapping deck — every card's name stays visible at the bottom */}
          <div
            className="relative mx-auto"
            style={{ width: stackW, maxWidth: '100%', height: CARD_H + maxTop + 16 }}
            data-testid="testimonials-stack"
          >
            {testimonials.map((item, index) => {
              const rel = (index - activeIndex + total) % total;
              const slot = SLOTS[rel];
              const isActive = rel === 0;
              return (
                <motion.div
                  key={item.id}
                  data-testid={`testimonial-card-${item.id}`}
                  onClick={() => !isActive && setActiveIndex(index)}
                  drag={isActive ? 'x' : false}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.18}
                  dragSnapToOrigin
                  onDragEnd={(e, info) => {
                    if (info.offset.x < -55) next();
                    else if (info.offset.x > 55) prev();
                  }}
                  className="absolute bg-white shadow-xl border-l-4 border-[#d4ff00] p-6 lg:p-8 flex flex-col cursor-pointer"
                  style={{ height: CARD_H, width: cardW, touchAction: 'pan-y' }}
                  initial={false}
                  animate={{
                    top: slot.top,
                    left: slot.left,
                    rotate: slot.rotate,
                    zIndex: slot.z,
                    scale: isActive ? 1 : 0.99,
                  }}
                  transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <CardInner item={item} />
                </motion.div>
              );
            })}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-5 mt-8">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous testimonial"
              data-testid="testimonial-prev"
              className="w-10 h-10 flex items-center justify-center border border-neutral-300 rounded-full text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 transition-colors"
            >
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <span className="text-[11px] tracking-[0.3em] uppercase text-neutral-400">Tap or swipe</span>
            <button
              type="button"
              onClick={next}
              aria-label="Next testimonial"
              data-testid="testimonial-next"
              className="w-10 h-10 flex items-center justify-center border border-neutral-300 rounded-full text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 transition-colors"
            >
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
